import { ImageIcon } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

interface RecordLimitIndicatorProps {
  recordsCount: number;
  maxRecordCount: number;
}

/**
 * The project's photo usage as a filled card: count out of the limit, how many
 * photos are left and how full the project is. See [[record-limit-indicator.spec]].
 */
export function RecordLimitIndicator({
  recordsCount,
  maxRecordCount,
}: RecordLimitIndicatorProps) {
  const isUnlimited = maxRecordCount === 0;

  const percentage = isUnlimited
    ? 0
    : Math.min(Math.round((recordsCount / maxRecordCount) * 100), 100);
  const left = Math.max(maxRecordCount - recordsCount, 0);
  const isAtLimit = !isUnlimited && recordsCount >= maxRecordCount;
  const isDanger = !isUnlimited && percentage >= 90;
  const isWarning = !isUnlimited && !isDanger && percentage >= 75;

  return (
    <div
      data-testid="record-limit-indicator"
      className="flex flex-col rounded-xl bg-muted px-3 py-2.5"
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <ImageIcon aria-hidden="true" className="size-[17px] text-muted-foreground" />
        <span>Photos</span>
        <span className="ml-auto tabular-nums">
          {recordsCount.toLocaleString()}
          {!isUnlimited && (
            <span className="text-[13px] font-normal text-muted-foreground">
              {" "}
              / {maxRecordCount.toLocaleString()}
            </span>
          )}
        </span>
      </div>
      {isUnlimited ? (
        <span className="mt-1.5 text-xs text-muted-foreground">Unlimited</span>
      ) : (
        <>
          <Progress
            value={percentage}
            className={cn(
              "my-2 h-1.5 w-full",
              isDanger
                ? "bg-destructive/20 [&>[data-slot=progress-indicator]]:bg-destructive"
                : isWarning
                  ? "bg-amber-500/20 [&>[data-slot=progress-indicator]]:bg-amber-500"
                  : "",
            )}
          />
          <div className="flex justify-between text-xs text-muted-foreground tabular-nums">
            <span className={cn((isDanger || isAtLimit) && "text-destructive")}>
              {isAtLimit ? "Limit reached" : `${left.toLocaleString()} left`}
            </span>
            <span>{percentage}% used</span>
          </div>
        </>
      )}
    </div>
  );
}
